import { useState } from 'react';
import { useApp, useFetch } from '../store/app';
import { Fade, Tabs } from './common';
import { Icon } from '../components/Icon';
import { Card, Button, StatusBadge } from '../components/primitives';
import { SideModal, LabeledText, LabeledArea, LabeledSelect } from '../components/SideModal';
import { st } from '../lib/status';
import { api } from '../lib/api';
import { fmtTimestamp } from '../lib/format';
import { EMAIL_EVENTS, RECIPIENT_OPTIONS, eventLabel, recipientLabel } from '../../shared/email';
import type { EmailTemplate, EmailTrigger, EmailLogEntry } from '../../shared/types';

type Tab = 'templates' | 'triggers' | 'outbox';

// All variables across every event, for the template editor helper.
const ALL_VARS = Array.from(new Set(EMAIL_EVENTS.flatMap((e) => e.vars))).sort();

export function Email() {
  const { refresh } = useApp();
  const [tab, setTab] = useState<Tab>('templates');
  const { data: config } = useFetch<{ configured: boolean; from: string }>('/email/config');
  const [testing, setTesting] = useState(false);

  const sendTest = async () => {
    const to = prompt('Send a test email to:', '');
    if (!to) return;
    setTesting(true);
    try {
      await api.post('/email/test', { to });
      alert(`Test email sent to ${to}.`);
      refresh();
    } catch (e) {
      alert(`Test failed: ${e instanceof Error ? e.message : 'unknown error'}`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <Fade>
      {config && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)', background: config.configured ? 'var(--success-bg)' : 'var(--warning-bg)' }}>
          <Icon name={config.configured ? 'mark_email_read' : 'schedule_send'} size={20} color={config.configured ? 'var(--success)' : 'var(--warning)'} />
          <div style={{ fontSize: 13, color: 'var(--text)' }}>
            {config.configured
              ? <>Delivery is <strong>live</strong> via Cloudflare Email Service — sending as <span className="cc-mono">{config.from}</span>.</>
              : <>Delivery <strong>not configured</strong> — messages are queued in the Outbox. Set <span className="cc-mono">CF_ACCOUNT_ID</span>, <span className="cc-mono">CF_EMAIL_TOKEN</span>, <span className="cc-mono">EMAIL_FROM</span> in Pages variables.</>}
          </div>
          <div style={{ flex: 1 }} />
          {config.configured && <Button variant="secondary" icon="send" onClick={sendTest} disabled={testing} style={{ padding: '6px 12px' }}>{testing ? 'Sending…' : 'Send test'}</Button>}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Tabs value={tab} onChange={(v) => setTab(v as Tab)} tabs={[{ label: 'Templates', value: 'templates' }, { label: 'Triggers', value: 'triggers' }, { label: 'Outbox', value: 'outbox' }]} />
      </div>
      {tab === 'templates' && <Templates />}
      {tab === 'triggers' && <Triggers />}
      {tab === 'outbox' && <Outbox />}
    </Fade>
  );
}

/* ---------------- Templates ---------------- */
function Templates() {
  const { refresh } = useApp();
  const { data } = useFetch<EmailTemplate[]>('/email/templates');
  const [editing, setEditing] = useState<EmailTemplate | 'new' | null>(null);
  const templates = data || [];

  return (
    <>
      <Card style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '13px 16px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Email templates</div>
          <div style={{ flex: 1 }} />
          <Button icon="add" onClick={() => setEditing('new')}>New template</Button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 2fr 0.6fr 40px', gap: 14, padding: '10px 18px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
          <div>Name</div><div>Subject</div><div>Status</div><div /></div>
        {templates.map((t) => (
          <div key={t.id} onClick={() => setEditing(t)} className="cc-hover-s2 cc-clickable" style={{ display: 'grid', gridTemplateColumns: '1.4fr 2fr 0.6fr 40px', gap: 14, padding: '12px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{t.name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</div>
            <div><StatusBadge badge={t.active ? st('connected') : st('draft')} /></div>
            <div style={{ textAlign: 'right' }}><Icon name="edit" size={18} color="var(--text-3)" /></div>
          </div>
        ))}
        {!templates.length && <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No templates yet.</div>}
      </Card>
      {editing && <TemplateEditor template={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { refresh(); setEditing(null); }} />}
    </>
  );
}

function TemplateEditor({ template, onClose, onSaved }: { template: EmailTemplate | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(template?.name || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [body, setBody] = useState(template?.body || '');
  const [description, setDescription] = useState(template?.description || '');
  const [active, setActive] = useState(template ? !!template.active : true);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { name, subject, body, description, active };
      if (template) await api.put(`/email/templates/${template.id}`, payload);
      else await api.post('/email/templates', payload);
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  const del = async () => {
    if (!template || !confirm('Delete this template?')) return;
    await api.del(`/email/templates/${template.id}`);
    onSaved();
  };

  return (
    <SideModal
      title={template ? 'Edit template' : 'New template'}
      sub="Use {{variable}} placeholders — they’re filled in when the email is sent."
      wide
      onClose={onClose}
      footer={
        <>
          {template ? <Button variant="danger" onClick={del}>Delete</Button> : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy || !name}>{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </>
      }
    >
      <LabeledText label="Template name" value={name} onChange={setName} placeholder="e.g. Welcome new staff" />
      <LabeledText label="Subject" value={subject} onChange={setSubject} placeholder="Welcome to {{org_name}}, {{name}}!" />
      <LabeledArea label="Body" value={body} onChange={setBody} rows={9} placeholder="Hi {{name}}, …" />
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 7 }}>Insert variable</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {ALL_VARS.map((v) => (
            <span key={v} onClick={() => setBody((b) => `${b}{{${v}}}`)} className="cc-mono cc-clickable" style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', padding: '3px 8px', borderRadius: 6 }}>{`{{${v}}}`}</span>
          ))}
        </div>
      </div>
      <LabeledArea label="Description (internal)" value={description} onChange={setDescription} rows={2} placeholder="When is this used?" />
      <Toggle label="Active" on={active} onToggle={() => setActive((a) => !a)} />
    </SideModal>
  );
}

/* ---------------- Triggers ---------------- */
function Triggers() {
  const { refresh } = useApp();
  const { data } = useFetch<EmailTrigger[]>('/email/triggers');
  const { data: templates } = useFetch<EmailTemplate[]>('/email/templates');
  const [editing, setEditing] = useState<EmailTrigger | 'new' | null>(null);
  const triggers = data || [];

  const toggle = async (t: EmailTrigger) => {
    await api.patch(`/email/triggers/${t.id}`, { enabled: !t.enabled });
    refresh();
  };

  return (
    <>
      <Card style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '13px 16px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Triggers</div>
          <div style={{ flex: 1 }} />
          <Button icon="add" onClick={() => setEditing('new')}>New trigger</Button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 1.2fr 0.6fr 0.7fr 40px', gap: 14, padding: '10px 18px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
          <div>When (event)</div><div>Template</div><div>Recipients</div><div>Order</div><div>Enabled</div><div /></div>
        {triggers.map((t) => (
          <div key={t.id} onClick={() => setEditing(t)} className="cc-hover-s2 cc-clickable" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 1.2fr 0.6fr 0.7fr 40px', gap: 14, padding: '12px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{eventLabel(t.event)}</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{t.template_name || '—'}</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{t.recipient === 'custom' ? t.custom_email : recipientLabel(t.recipient)}</div>
            <div className="cc-mono" style={{ fontSize: 13, color: 'var(--text-3)' }}>{t.sort}</div>
            <div onClick={(e) => { e.stopPropagation(); toggle(t); }}><Toggle on={t.enabled} onToggle={() => {}} compact /></div>
            <div style={{ textAlign: 'right' }}><Icon name="edit" size={18} color="var(--text-3)" /></div>
          </div>
        ))}
        {!triggers.length && <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No triggers yet.</div>}
      </Card>
      {editing && <TriggerEditor trigger={editing === 'new' ? null : editing} templates={templates || []} onClose={() => setEditing(null)} onSaved={() => { refresh(); setEditing(null); }} />}
    </>
  );
}

function TriggerEditor({ trigger, templates, onClose, onSaved }: { trigger: EmailTrigger | null; templates: EmailTemplate[]; onClose: () => void; onSaved: () => void }) {
  const [event, setEvent] = useState(trigger?.event || EMAIL_EVENTS[0].key);
  const [templateId, setTemplateId] = useState(String(trigger?.template_id || templates[0]?.id || ''));
  const [recipient, setRecipient] = useState(trigger?.recipient || 'subject');
  const [customEmail, setCustomEmail] = useState(trigger?.custom_email || '');
  const [sort, setSort] = useState(String(trigger?.sort || 1));
  const [enabled, setEnabled] = useState(trigger ? trigger.enabled : true);
  const [busy, setBusy] = useState(false);

  const ev = EMAIL_EVENTS.find((e) => e.key === event);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { event, template_id: Number(templateId) || null, recipient, custom_email: customEmail, sort: Number(sort) || 1, enabled };
      if (trigger) await api.put(`/email/triggers/${trigger.id}`, payload);
      else await api.post('/email/triggers', payload);
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  const del = async () => {
    if (!trigger || !confirm('Delete this trigger?')) return;
    await api.del(`/email/triggers/${trigger.id}`);
    onSaved();
  };

  return (
    <SideModal
      title={trigger ? 'Edit trigger' : 'New trigger'}
      sub="Pick an event, the template to send, and who receives it."
      onClose={onClose}
      footer={
        <>
          {trigger ? <Button variant="danger" onClick={del}>Delete</Button> : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </>
      }
    >
      <LabeledSelect label="When this happens" value={event} onChange={setEvent} options={EMAIL_EVENTS.map((e) => ({ label: e.label, value: e.key }))} />
      {ev && <div style={{ fontSize: 12, color: 'var(--text-3)', margin: '-8px 0 14px', lineHeight: 1.5 }}>{ev.description} Variables: {ev.vars.map((v) => `{{${v}}}`).join(', ')}</div>}
      <LabeledSelect label="Send this template" value={templateId} onChange={setTemplateId} options={templates.map((t) => ({ label: t.name, value: String(t.id) }))} />
      <LabeledSelect label="To" value={recipient} onChange={setRecipient} options={RECIPIENT_OPTIONS.map((r) => ({ label: r.label, value: r.key }))} />
      {recipient === 'custom' && <LabeledText label="Custom email address" value={customEmail} onChange={setCustomEmail} placeholder="name@company.com" />}
      <LabeledText label="Send order (lower goes first)" value={sort} onChange={setSort} />
      <Toggle label="Enabled" on={enabled} onToggle={() => setEnabled((e) => !e)} />
    </SideModal>
  );
}

/* ---------------- Outbox ---------------- */
function Outbox() {
  const { refresh } = useApp();
  const { data } = useFetch<EmailLogEntry[]>('/email/log');
  const [view, setView] = useState<EmailLogEntry | null>(null);
  const log = data || [];

  const clear = async () => {
    if (!confirm('Clear the outbox log?')) return;
    await api.del('/email/log');
    refresh();
  };

  return (
    <>
      <Card style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '13px 16px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Outbox</div>
          <span style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 8 }}>Queued messages (delivery wired in Phase 2)</span>
          <div style={{ flex: 1 }} />
          {log.length > 0 && <Button variant="secondary" icon="delete" onClick={clear}>Clear</Button>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 2fr 1.2fr 0.7fr 1fr', gap: 14, padding: '10px 18px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
          <div>To</div><div>Subject</div><div>Event</div><div>Status</div><div>When</div></div>
        {log.map((e) => (
          <div key={e.id} onClick={() => setView(e)} className="cc-hover-s2 cc-clickable" style={{ display: 'grid', gridTemplateColumns: '1.6fr 2fr 1.2fr 0.7fr 1fr', gap: 14, padding: '12px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
            <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.to_name ? `${e.to_name} · ` : ''}{e.to_email}</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.subject}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{eventLabel(e.event)}</div>
            <div><StatusBadge badge={e.status === 'sent' ? st('paid') : e.status === 'failed' ? st('overdue') : st('pending')} /></div>
            <div className="cc-mono" style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{fmtTimestamp(e.created_at)}</div>
          </div>
        ))}
        {!log.length && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No emails queued yet. Trigger one by adding a person, submitting an invoice, or completing an onboarding step.</div>}
      </Card>
      {view && (
        <SideModal title={view.subject || '(no subject)'} sub={`${eventLabel(view.event)} → ${view.to_name ? view.to_name + ' · ' : ''}${view.to_email}`} onClose={() => setView(null)}>
          {view.status === 'failed' && view.error && (
            <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 8, background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: 12.5 }}>
              <strong>Delivery failed:</strong> {view.error}
            </div>
          )}
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, color: 'var(--text)', lineHeight: 1.7 }}>{view.body}</div>
        </SideModal>
      )}
    </>
  );
}

/* ---------------- shared bits ---------------- */
function Toggle({ on, onToggle, label, compact }: { on: boolean; onToggle: () => void; label?: string; compact?: boolean }) {
  const sw = (
    <div onClick={onToggle} className="cc-clickable" style={{ width: 38, height: 22, borderRadius: 20, background: on ? 'var(--accent)' : 'var(--surface-3)', position: 'relative', flex: '0 0 auto' }}>
      <div style={{ position: 'absolute', top: 2, [on ? 'right' : 'left']: 2, width: 18, height: 18, borderRadius: '50%', background: '#fff' }} />
    </div>
  );
  if (compact) return sw;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {sw}
      <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{label}</span>
    </div>
  );
}
